import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DatePicker,
  Input,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { FileSpreadsheet, Loader2, RotateCcw, Wallet, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { ALL_CLINICS } from '../../hooks/useAccessibleClinics';
import type { PayrollFilters } from '../../store/slices/payrollSlice';
import {
  createPayrollEntry,
  exportPayrollVisits,
  listPayrollVisits,
} from '../../services/api';
import type { PayrollVisitItem } from '../../services/api/types.gen';
import type { EmployeeType } from '../../services/api/models/EmployeeType';
import { EMPLOYEE_TYPE_OPTIONS } from '../../constants/employeeTypes';
import { useToast } from '../../hooks/useToastContext';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../../utils/apiErrorDetail';
import { getAgencyClinicDisplayName } from '../../utils/agencyClinicDisplayName';
import {
  formatDateToISO,
  parseISOToLocalDate,
  getBusinessTodayDate,
} from '../../utils/dateUtils';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import { compareClinicOptions } from '../../utils/clinicSelect';
import { getAgencyClinicScope } from '../../services/clinicsApi';
import { ClinicSelect } from '../clinic/ClinicSelect';
import { PaginationBar } from './PaginationBar';
import { PayrollVisitsTable } from './PayrollVisitsTable';

const PAGE_SIZE = 25;

type PayrollFilterQuery = {
  clinic_id: string | typeof ALL_CLINICS;
  start_date: string;
  end_date: string;
  employee_types?: EmployeeType[];
  search?: string;
  hide_imported_patients: boolean;
};

function serializePayrollQuery(
  filters: PayrollFilters
): PayrollFilterQuery | null {
  if (!filters.clinicId || !filters.startDate || !filters.endDate) return null;
  return {
    clinic_id: filters.clinicId,
    start_date: filters.startDate,
    end_date: filters.endDate,
    // An empty selection means All. Sending every explicit type keeps
    // unassigned clinicians out of a deliberately narrowed selection.
    employee_types:
      filters.employeeTypes.length > 0 ? filters.employeeTypes : undefined,
    search: filters.search || undefined,
    hide_imported_patients: filters.hideImportedPatients !== false,
  };
}

/** Visits not yet in any payroll — filter, select, and add them to a payroll. */
export const UnpaidPayrollTab: React.FC = () => {
  const {
    payrollFilters,
    setPayrollFilters,
    initialDefaultsApplied,
    setInitialDefaultsApplied,
    agencyClinics,
    agencyClinicsLoading,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      payrollFilters: state.payrollFilters,
      setPayrollFilters: state.setPayrollFilters,
      initialDefaultsApplied: state.initialDefaultsApplied,
      setInitialDefaultsApplied: state.setInitialDefaultsApplied,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );
  const toast = useToast();

  const [visits, setVisits] = useState<PayrollVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  // The list stays empty until the user clicks "Find Unpaid Visits"; after
  // that first fetch, changing a filter refetches on its own — until one of
  // the three required filters is cleared, which returns the tab to this
  // pre-fetch state and requires clicking the button again.
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Keep a saved All Clinics selection until the async scope check resolves.
  // A restricted result (or a failed check) is the only value that should
  // force it back to an authorized clinic.
  const [hasFullClinicScope, setHasFullClinicScope] = useState<boolean | null>(
    null
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState(payrollFilters.search);
  // Monotonic fetch generation; only the newest may write results.
  const requestIdRef = useRef(0);
  // Bumped to ask for a refetch with whatever filters are current *now*.
  const [refreshToken, setRefreshToken] = useState(0);

  /** The one definition of "no results" — reset and clear-filters share it. */
  const clearResults = useCallback(() => {
    setVisits([]);
    setSelectedIds(new Set());
    setTotal(0);
    setTotalPages(1);
    setHasFetched(false);
  }, []);

  /**
   * Clear, and make any in-flight fetch unable to write its result.
   *
   * For callers outside `fetchVisits` (Reset), which would otherwise be
   * repopulated by a request that started before them and still matches the
   * current generation. Not used on the error path: that request *is* the
   * current generation, and retiring it there would double-clear the spinner.
   *
   * Retiring the generation also takes over the spinner: the in-flight fetch's
   * `finally` only clears it for the *current* generation, so without this the
   * tab would sit on "Loading visits…" forever — Reset also clears the required
   * filters, which disables the one button that could start another fetch.
   */
  const discardResults = useCallback(() => {
    requestIdRef.current += 1;
    clearResults();
    setIsLoading(false);
  }, [clearResults]);

  useEffect(() => {
    fetchAgencyClinics();
    getAgencyClinicScope()
      .then(scope =>
        setHasFullClinicScope(scope.has_full_clinic_scope === true)
      )
      .catch(error => {
        console.error('Error fetching agency clinic scope:', error);
        setHasFullClinicScope(false);
      });
  }, [fetchAgencyClinics]);

  // Keep the initial report bounded to the first authorized clinic and the
  // same Pacific calendar window used by the backend.
  useEffect(() => {
    const today = getBusinessTodayDate();
    const start = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 30
    );
    const sortedClinics = agencyClinics
      .map(clinic => ({
        value: clinic.id,
        label: getAgencyClinicDisplayName(clinic, {
          totalClinicCount: agencyClinics.length,
        }),
      }))
      .sort(compareClinicOptions);
    const clinicId = agencyClinics.length
      ? sortedClinics.some(
          clinic => clinic.value === payrollFilters.clinicId
        ) ||
        (hasFullClinicScope !== false &&
          payrollFilters.clinicId === ALL_CLINICS)
        ? payrollFilters.clinicId
        : (sortedClinics[0]?.value ?? null)
      : payrollFilters.clinicId === ALL_CLINICS
        ? ALL_CLINICS
        : null;
    const applyDateDefaults =
      !initialDefaultsApplied && agencyClinics.length > 0;
    if (applyDateDefaults) {
      setInitialDefaultsApplied(true);
    }
    setPayrollFilters({
      clinicId,
      ...(applyDateDefaults
        ? {
            startDate: formatDateToISO(start),
            endDate: formatDateToISO(today),
          }
        : {}),
    });
  }, [
    agencyClinics,
    hasFullClinicScope,
    payrollFilters.clinicId,
    payrollFilters.startDate,
    payrollFilters.endDate,
    initialDefaultsApplied,
    setInitialDefaultsApplied,
    setPayrollFilters,
  ]);

  // Debounced server-side search (350ms), matching IntakeManager/OASISManager.
  useEffect(() => {
    if (searchInput === payrollFilters.search) return;

    const timer = setTimeout(() => {
      setPayrollFilters({ search: searchInput });
      setPage(1);
      setSelectedIds(new Set());
    }, 350);
    return () => clearTimeout(timer);
  }, [payrollFilters.search, searchInput, setPayrollFilters]);

  const filtersReady =
    hasFullClinicScope !== null &&
    !agencyClinicsLoading &&
    agencyClinics.length > 0 &&
    !!payrollFilters.clinicId &&
    !!payrollFilters.startDate &&
    !!payrollFilters.endDate;

  const rangeIsBackwards =
    !!payrollFilters.startDate &&
    !!payrollFilters.endDate &&
    payrollFilters.startDate > payrollFilters.endDate;

  const fetchVisits = useCallback(
    async (targetPage: number) => {
      if (rangeIsBackwards) return;

      // Claim a generation up front. Clearing a filter or switching clinics
      // supersedes whatever is in flight, and a late response must not write
      // its rows back over the newer state — otherwise the previous clinic's
      // visits reappear under a form that no longer selects them, and they
      // stay selectable. Also makes fast A→B switching order-independent.
      const requestId = ++requestIdRef.current;

      const query = serializePayrollQuery(payrollFilters);
      if (!query) {
        // Clearing a filter clears the results too. Leaving them up shows rows
        // that no longer match the form, and a surviving selection would let
        // "Mark as Paid" post visits the user can no longer see.
        clearResults();
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await listPayrollVisits({
          query: {
            ...query,
            page: targetPage,
            per_page: PAGE_SIZE,
          },
          throwOnError: true,
        });
        // A newer fetch (or a filter reset) started while this was in flight.
        if (requestId !== requestIdRef.current) return;
        if (response.data) {
          // A refetch can land past the last page (e.g. after adding visits
          // to a payroll shrank the list) — clamp and refetch once.
          if (
            targetPage > 1 &&
            response.data.items.length === 0 &&
            response.data.total > 0
          ) {
            const lastPage = response.data.total_pages;
            setPage(lastPage);
            return;
          }
          setVisits(response.data.items);
          setTotal(response.data.total);
          setTotalPages(response.data.total_pages);
          setHasFetched(true);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        // Drop whatever was on screen. These rows belong to the filters that
        // last succeeded, not the ones now in the form, and leaving them up
        // re-enables "Mark as Paid" over a stale selection once the spinner
        // clears — which would post visits the current filters never showed.
        clearResults();
        console.error('Failed to load payroll visits:', error);
        toast.error(
          getBusinessErrorMessage(error) ??
            'Failed to load payroll visits. Please try again.'
        );
      } finally {
        // The newest request owns the spinner; a superseded one must not
        // clear it while that fetch is still running.
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    // toast is intentionally omitted: useToast returns a fresh object every
    // render, so including it would refetch in a loop (OASISManager pattern).
    [
      payrollFilters.clinicId,
      payrollFilters.startDate,
      payrollFilters.endDate,
      payrollFilters.employeeTypes,
      payrollFilters.search,
      payrollFilters.hideImportedPatients,
      clearResults,
      rangeIsBackwards,
    ]
  );

  // After the first explicit fetch, page and search changes refetch.
  useEffect(() => {
    if (!hasFetched) return;
    fetchVisits(page);
  }, [hasFetched, page, fetchVisits, refreshToken]);

  // Selection is page-scoped: prune ids that left the visible page.
  useEffect(() => {
    const visibleIds = new Set(visits.map(visit => visit.visit_id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(visitId => visibleIds.has(visitId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [visits]);

  const handleCreateByDate = () => {
    if (!filtersReady || rangeIsBackwards) return;
    setSelectedIds(new Set());
    setPage(1);
    if (hasFetched) {
      fetchVisits(1);
    } else {
      setHasFetched(true);
    }
  };

  const handleResetFilters = () => {
    setPayrollFilters({
      clinicId: null,
      startDate: null,
      endDate: null,
      employeeTypes: [],
      search: '',
      hideImportedPatients: true,
    });
    setSearchInput('');
    setPage(1);
    discardResults();
  };

  const toggleSelect = (visitId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(visitId)) {
        next.delete(visitId);
      } else {
        next.add(visitId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.size === visits.length
        ? new Set()
        : new Set(visits.map(visit => visit.visit_id))
    );
  };

  const handleCreatePayroll = async () => {
    if (
      selectedIds.size === 0 ||
      !payrollFilters.clinicId ||
      payrollFilters.clinicId === ALL_CLINICS
    ) {
      return;
    }

    setIsCreating(true);
    try {
      const response = await createPayrollEntry({
        body: {
          visit_ids: Array.from(selectedIds),
          clinic_id: payrollFilters.clinicId,
        },
        throwOnError: true,
      });
      const { added_count, skipped_count } = response.data;
      toast.success(
        skipped_count > 0
          ? `Added ${added_count} visits to payroll (${skipped_count} skipped).`
          : `Added ${added_count} visits to payroll.`
      );
      setSelectedIds(new Set());
      // A direct await fetchVisits() could let an older request overwrite newer filter results.
      // The refresh-token state update drives the fetch for the latest generation.
      setRefreshToken(token => token + 1);
    } catch (error) {
      console.error('Failed to create payroll:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to create payroll. Please try again.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleExport = async () => {
    if (!filtersReady) return;
    setIsExporting(true);
    try {
      const query = serializePayrollQuery(payrollFilters);
      if (!query) return;
      const response = await exportPayrollVisits({
        query,
        responseType: 'blob',
      });
      if (response.error) {
        // The server explains an over-cap range and how to narrow it; a
        // generic message would leave the user retrying the same export.
        console.error('Failed to export payroll visits:', response.error);
        toast.error(
          await getUserFacingApiMessageAsync(
            response,
            'Failed to export. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response, 'RogerPayrollExport.xlsx');
    } catch (error) {
      console.error('Failed to export payroll visits:', error);
      toast.error(
        (await getUserFacingApiMessageAsync(
          error,
          'Failed to export. Please try again.'
        )) ?? 'Failed to export. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const allClinics = payrollFilters.clinicId === ALL_CLINICS;

  return (
    <div className='space-y-6'>
      {/* Filter Controls */}
      <div className='flex flex-wrap gap-4 items-end'>
        {/* Search absorbs leftover space; clinic gets double the growth weight */}
        <div className='flex-1 min-w-[180px]'>
          <label htmlFor='search' className='text-sm font-medium mb-1.5 block'>
            Search
          </label>
          <Input
            id='search'
            placeholder='Search by patient or clinician...'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>

        <div className='flex-[2] min-w-[260px] max-w-xl'>
          <ClinicSelect
            label='Clinic'
            clinics={agencyClinics}
            includeAllOption={hasFullClinicScope === true}
            showEmrPills={false}
            value={payrollFilters.clinicId ?? ''}
            onChange={e => {
              setPayrollFilters({ clinicId: e.target.value || null });
              setPage(1);
              // Drop the selection immediately rather than waiting for the
              // refetch to prune it — the rows belong to the old clinic.
              setSelectedIds(new Set());
            }}
            placeholder='Select a clinic'
          />
        </div>

        <div className='w-40'>
          <label
            htmlFor='startDate'
            className='text-sm font-medium mb-1.5 block'
          >
            Start Date
          </label>
          <DatePicker
            className='w-full'
            typable
            showClearButton
            date={
              payrollFilters.startDate
                ? parseISOToLocalDate(payrollFilters.startDate)
                : undefined
            }
            onDateChange={d => {
              setPayrollFilters({ startDate: d ? formatDateToISO(d) : null });
              setSelectedIds(new Set());
              setPage(1);
            }}
          />
        </div>

        <div className='w-40'>
          <label htmlFor='endDate' className='text-sm font-medium mb-1.5 block'>
            End Date
          </label>
          <DatePicker
            className='w-full'
            typable
            showClearButton
            date={
              payrollFilters.endDate
                ? parseISOToLocalDate(payrollFilters.endDate)
                : undefined
            }
            onDateChange={d => {
              setPayrollFilters({ endDate: d ? formatDateToISO(d) : null });
              setSelectedIds(new Set());
              setPage(1);
            }}
          />
        </div>

        <div className='w-48 space-y-1.5'>
          <span className='text-sm font-medium block'>Employee Type</span>
          <VirtualizedMultiSelectCombobox
            options={EMPLOYEE_TYPE_OPTIONS}
            value={payrollFilters.employeeTypes}
            onValueChange={values => {
              setPayrollFilters({
                employeeTypes: values as EmployeeType[],
              });
              setPage(1);
              setSelectedIds(new Set());
            }}
            placeholder='All'
            searchPlaceholder='Search employee type...'
            buttonClassName='w-full'
            ariaLabel='Employee Type'
          />
        </div>

        <label className='flex items-center gap-2 pb-2 text-sm'>
          <Checkbox
            checked={payrollFilters.hideImportedPatients !== false}
            onCheckedChange={checked => {
              setPayrollFilters({ hideImportedPatients: checked === true });
              setPage(1);
              setSelectedIds(new Set());
            }}
          />
          Hide imported patients
        </label>

        <Button onClick={handleCreateByDate} disabled={!filtersReady}>
          Find Unpaid Visits
        </Button>

        <Button
          variant='outline'
          onClick={handleExport}
          disabled={!filtersReady || isExporting}
          className='flex items-center gap-2'
        >
          {isExporting ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <FileSpreadsheet className='h-4 w-4' />
          )}
          Export to Excel
        </Button>

        <Button
          variant='outline'
          onClick={handleResetFilters}
          className='flex items-center gap-2'
        >
          <RotateCcw className='h-4 w-4' />
          Reset
        </Button>
      </div>

      {rangeIsBackwards && (
        <p className='text-sm text-destructive'>
          The From date must not be after the To date.
        </p>
      )}

      {/* Content */}
      {isLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>Loading visits...</span>
        </div>
      ) : !hasFetched ? (
        <div className='text-center text-muted-foreground py-8'>
          <Wallet className='h-12 w-12 mx-auto mb-4 text-gray-500' />
          <p>Select a clinic and date range</p>
          <p className='text-sm'>
            Then click Find Unpaid Visits to load eligible visits
          </p>
        </div>
      ) : visits.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <Wallet className='h-12 w-12 mx-auto mb-4 text-gray-500' />
          <p>No payroll-eligible visits found</p>
          <p className='text-sm'>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className='space-y-4'>
          {allClinics && (
            <Alert>
              <AlertDescription>
                Select a single clinic to select visits and mark them as paid.
              </AlertDescription>
            </Alert>
          )}
          <div className='rounded-md border'>
            <PayrollVisitsTable
              visits={visits}
              groupBy='clinician'
              showStatusColumn
              showClinicColumn={allClinics}
              {...(allClinics
                ? {}
                : {
                    selectedIds,
                    onToggleSelect: toggleSelect,
                    onToggleSelectAll: toggleSelectAll,
                  })}
            />
          </div>

          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            itemsLabel='visits'
            onPageChange={setPage}
            disabled={isLoading}
          />
        </div>
      )}

      {/* Floating selection bar (IntakeSelectionActions pattern) */}
      {!allClinics && selectedIds.size > 0 && (
        <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50'>
          <div className='bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg flex items-center gap-2'>
            <div className='flex items-center gap-2 h-7 px-2 bg-black/20 rounded border border-white/10'>
              <span className='text-sm'>{selectedIds.size} selected</span>
              <Button
                onClick={() => setSelectedIds(new Set())}
                size='icon'
                variant='ghost'
                className='hover:bg-black/20'
                aria-label='Clear selection'
              >
                <X className='w-3.5 h-3.5' />
              </Button>
            </div>
            {payrollFilters.clinicId !== ALL_CLINICS && (
              <Button
                variant='ghost'
                onClick={handleCreatePayroll}
                // Also blocked while loading: the bar stays mounted over the
                // spinner, so without this a click mid-refetch posts the
                // outgoing clinic's ids against the incoming clinic.
                disabled={isCreating || isLoading}
                className='h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 hover:text-primary-foreground'
              >
                {isCreating && <Loader2 className='h-4 w-4 animate-spin' />}
                Mark as Paid
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
